import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { UserAccountType } from 'src/app/interfaces/models/user-account-type';

import { BaseModel } from './base-model';
import { Company } from './company';
import { CompanyVendorFile } from './company-vendor-file';
import { Contact } from './contact';
import { ProposalRequest } from './proposal-request';
import { SiteUser } from './site-user';
import { UserAccountRole } from './user-account-role';
import { Vendor } from './vendor';
import { VendorReview } from './vendor-review';

export class User extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static UserProperties: (
    | {
        property: keyof User;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof User;
        type: 'alias';
      }
    | {
        property: keyof User;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'firstName', type: 'column', dataType: 'string' },
    { property: 'lastName', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'email', type: 'column', dataType: 'string' },
    { property: 'jobTitle', type: 'column', dataType: 'string' },
    { property: 'extension', type: 'column', dataType: 'string' },
    { property: 'mobilePhone', type: 'column', dataType: 'string' },
    { property: 'mobilePhoneExtension', type: 'column', dataType: 'string' },
    { property: 'registrationStatus', type: 'column', dataType: 'string' },
    { property: 'hasLoginPrivilege', type: 'column', dataType: 'boolean' },
    { property: 'lastLoginDate', type: 'column', dataType: 'date' },
    { property: 'canBeContacted', type: 'column', dataType: 'boolean' },
    { property: 'deleted', type: 'column', dataType: 'boolean' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'ContactId', type: 'column', dataType: 'number' },
    { property: 'UserAccountTypeId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'UserAccountRoleId', type: 'column', dataType: 'number' },
    { property: 'isExternalViveEmployee', type: 'column', dataType: 'boolean' },
    { property: 'isSuperUser', type: 'column', dataType: 'boolean' },
    { property: 'canWaive', type: 'column', dataType: 'boolean' },
    { property: 'allowTexts', type: 'column', dataType: 'boolean' },
    {
      property: 'isTermsAndConditionsConfirmed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'termsAndConditionsConfirmedDate',
      type: 'column',
      dataType: 'date'
    },
    {
      property: 'sendWeeklyStatusUpdates',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'sendDailyStatusUpdates', type: 'column', dataType: 'boolean' },

    { property: 'fullName', type: 'alias' },
    { property: 'contactTypes', type: 'alias' },
    { property: 'siteAssignments', type: 'alias' },
    { property: 'userRole', type: 'alias' },
    { property: 'companyName', type: 'alias' },

    {
      property: 'UserAccountType',
      model: 'UserAccountType',
      type: 'association'
    },
    {
      property: 'UserAccountRole',
      model: 'UserAccountRole',
      type: 'association'
    },
    { property: 'Contact', model: 'Contact', type: 'association' },
    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'VendorReviews', model: 'VendorReview', type: 'association' },
    {
      property: 'ProposalRequests',
      model: 'ProposalRequest',
      type: 'association'
    },
    {
      property: 'CompanyVendorFiles',
      model: 'CompanyVendorFile',
      type: 'association'
    },
    { property: 'SiteUser', model: 'SiteUser', type: 'association' }
  ];

  static {
    User.UserProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        User.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        User.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        User.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(UserObject: Partial<User>) {
    super();

    User.UserProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = UserObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number | null;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  jobTitle?: string | null;
  extension?: string | null;
  mobilePhone?: string | null;
  mobilePhoneExtension?: string | null;
  registrationStatus?: 'unconfirmed' | 'confirmed' | null;
  hasLoginPrivilege?: boolean | null;
  lastLoginDate?: Date | null;
  canBeContacted?: boolean | null;
  deleted?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  CompanyId?: number | null;
  ContactId?: number | null;
  UserAccountTypeId?: number;
  VendorId?: number | null;
  UserAccountRoleId?: number;
  isExternalViveEmployee?: boolean | null;
  isSuperUser?: boolean;
  allowTexts?: boolean;
  isTermsAndConditionsConfirmed?: boolean;
  termsAndConditionsConfirmedDate?: Date | null;
  sendWeeklyStatusUpdates?: boolean;
  sendDailyStatusUpdates?: boolean;
  canWaive?: boolean;

  // Aliased columns
  fullName?: string;
  contactTypes?: string;
  siteAssignments?: string;
  // This is a formatted field representing the user's permission role.
  userRole?: string;
  companyName?: string;

  Contact?: Contact;
  Company?: Company;
  Vendor?: Vendor;
  UserAccountType?: UserAccountType;
  UserAccountRole?: UserAccountRole;
  VendorReviews?: VendorReview[];
  ProposalRequests?: ProposalRequest[];
  CompanyVendorFiles?: CompanyVendorFile[];
  SiteUser?: SiteUser;

  // Special property.
  // This property cannot be retrieved through SQL. It is applied in special cases on backend.
  aliasUser?: boolean;

  // JWT-only claim (not a SQL column): HMAC-SHA256 of the user id, used as
  // Chameleon's uid_hash for secure identity verification in chmln.identify.
  chameleonUidHash?: string;
}
